import { Box, Divider, Link, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { parseAnswer, parseInline } from './answerFormat';

// Portal pages open in the same tab through the router, so the chat and its
// conversation stay; our own site opens in a new tab (PPS-405).
const Inline = ({ text, onPortalLink }) => {
    const history = useHistory();
    return parseInline(text).map((part, index) => {
        const content = part.bold ? <strong>{part.text}</strong> : part.text;
        if (!part.href) return <span key={index}>{content}</span>;
        const portal = !part.external;
        return (
            <Link
                key={index}
                href={part.href}
                {...(portal
                    ? {
                          onClick: (event) => {
                              // A modified click (new tab, new window) keeps the browser's own behaviour.
                              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                              event.preventDefault();
                              history.push(part.href);
                              onPortalLink?.();
                          }
                      }
                    : { target: '_blank', rel: 'noopener noreferrer' })}
                // The theme's light blue nearly disappears on the panel's light
                // blue, so links use the panel's deep blue and are always underlined.
                sx={{
                    wordBreak: 'break-all',
                    color: '#0a4c90',
                    fontWeight: 700,
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    '&:hover': { textDecorationThickness: '2px' }
                }}
            >
                {content}
            </Link>
        );
    });
};

/** A bot answer, with the model's Markdown rendered as React elements. */
const AnswerText = ({ text, onPortalLink }) => (
    <Box sx={{ '& > :first-of-type': { mt: 0 }, '& > :last-child': { mb: 0 } }}>
        {parseAnswer(text).map((block, index) => {
            if (block.type === 'rule') return <Divider key={index} sx={{ my: 1 }} />;
            if (block.type === 'heading') {
                return (
                    <Typography key={index} variant="body2" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5, color: 'inherit' }}>
                        <Inline text={block.text} onPortalLink={onPortalLink} />
                    </Typography>
                );
            }
            if (block.type === 'list') {
                return (
                    <Box key={index} component={block.ordered ? 'ol' : 'ul'} sx={{ pl: 2.5, my: 0.75 }}>
                        {block.items.map((item, itemIndex) => (
                            <Typography key={itemIndex} component="li" variant="body2" sx={{ color: 'inherit', mb: 0.25 }}>
                                <Inline text={item} onPortalLink={onPortalLink} />
                            </Typography>
                        ))}
                    </Box>
                );
            }
            // Paragraphs are justified to both edges, like the welcome text (design
            // team). Headings and lists stay left-aligned.
            return (
                <Typography
                    key={index}
                    variant="body2"
                    sx={{ my: 0.75, color: 'inherit', textAlign: 'justify', textJustify: 'inter-character' }}
                >
                    {block.lines.map((line, lineIndex) => (
                        <span key={lineIndex}>
                            {lineIndex > 0 && <br />}
                            <Inline text={line} onPortalLink={onPortalLink} />
                        </span>
                    ))}
                </Typography>
            );
        })}
    </Box>
);

export default AnswerText;
